import { useMemo } from 'react';
import {
  EntityType,
  OptionDTO,
  usePortfolioReportParamsQuery,
} from '../../api/usePortfolioReportParamsQuery';
import { AsyncComponentHookResult } from '../../../Types/ComponentHookResult';

const IconMap: Record<EntityType, string> = {
  [EntityType.Household]: '',
  [EntityType.Registration]: 'fas fa-address-card',
  [EntityType.Account]: 'far fa-briefcase',
  [EntityType.PortfolioGroup]: 'fas fa-boxes-alt',
};

const EntityTypeNameMap: Record<EntityType, string> = {
  [EntityType.Household]: 'Household',
  [EntityType.Registration]: 'Registration',
  [EntityType.Account]: 'Account',
  [EntityType.PortfolioGroup]: 'Portfolio Group',
};

interface EntityOption extends OptionDTO {
  subLabel?: string;
  icon?: string;
}

interface UseEntitySelectParams {
  paramId: string;
  selectedEntityType: string | null;
}

interface UseEntitySelectData {
  selectedOption: OptionDTO | null;
  options: EntityOption[] | null;
  placeholder: string;
}

export const useEntitySelect = ({
  paramId,
  selectedEntityType,
}: UseEntitySelectParams): AsyncComponentHookResult<UseEntitySelectData> => {
  const queryInfo = usePortfolioReportParamsQuery();

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const param = queryInfo.data.params[paramId];

    const value = param.values ? param.values[0] : null;

    if (!param.options || !selectedEntityType) {
      return null;
    }

    const options = param.options
      .filter(({ entityType }) => entityType === selectedEntityType)
      .map(option => ({
        ...option,
        subLabel: option.description,
        icon: option.entityType ? IconMap[option.entityType] : undefined,
      }));

    const selectedOption = value ? options.find(({ value: _value }) => value === _value) : null;

    const placeholder = `Select ${EntityTypeNameMap[selectedEntityType]}`;

    return {
      selectedOption: selectedOption || null,
      options,
      placeholder,
    };
  }, [paramId, queryInfo.data, selectedEntityType]);

  return {
    ...queryInfo,
    data,
  };
};
